/**
 * GDSEnvironmentStepRenderer.js
 * Renders the environment selection step (Step 2).
 */
export class GDSEnvironmentStepRenderer {
    constructor(orchestrator) {
        this.orchestrator = orchestrator;
        this.builder = orchestrator.getBuilder();
        this.state = this.builder.stateManager;
    }

    get contentContainer() {
        return this.builder?.contentContainer || null;
    }

    async render() {
        const { options: environmentOptions = [], context = {} } = this.state.context;

        if (environmentOptions.length === 0) {
            this.contentContainer.innerHTML = `<div class="p-6"><p class="text-gray-700">No environment options are available for this device.</p></div>`;
            return;
        }

        const environmentDetails = this._getEnvironmentDetailsMap();
        const sortedOptions = this._getSortedEnvironmentOptions(environmentOptions);
        const gridLayout = this._getEnvironmentGridLayout(sortedOptions.length);

        const cardsHTML = sortedOptions.map(envOption =>
            this._buildEnvironmentCardHTML(envOption, environmentDetails)
        ).join('');

        const modalsHTML = sortedOptions.map(envOption =>
            this._buildEnvironmentModalHTML(envOption, environmentDetails)
        ).join('');

        const stepHTML = this._buildEnvironmentStepHTML(context, cardsHTML, modalsHTML, gridLayout);
        this.contentContainer.innerHTML = stepHTML;
    }

    _buildEnvironmentStepHTML(context, cardsHTML, modalsHTML, gridLayout) {
        return `
            <div class="p-0 md:p-6 px-6">
                <form class="space-y-6" action="javascript:void(0);">
                    <div>
                        <h2 class="text-xl font-bold text-gray-900 hidden md:block pb-8">
                            ${context['Device Make'] || ''} ${context['Device Model'] || ''}
                        </h2>
                        <div class="grid ${gridLayout.gridColsClass} gap-6 ${gridLayout.gridContainerClass}">
                            ${cardsHTML}
                        </div>
                         <input type="hidden" data-field="environment" name="environment" value="">
                    </div>
                </form>
                <div class="modals-container">${modalsHTML}</div>
            </div>
        `;
    }

    _getEnvironmentDetailsMap() {
        return {
            "Light Duty": {
                "Light Duty Use": {
                    image: "https://cdn.shopify.com/s/files/1/0593/5346/4982/files/LightDutyUse.jpg?v=1745854127",
                    features: ["Non-Vehicle Applications", "Office / Workstation", "Healthcare", "Food Service", "Retail / Hospitality"],
                    modalInfo: {
                        title: "Light Duty Environment",
                        description: "Light Duty environments are static settings where devices remain in a fixed location. These environments typically have minimal movement, vibration, and exposure to elements.",
                        idealFor: ["Retail stores", "Office buildings", "Healthcare facilities", "Hotels & restaurants"],
                        options: [
                            "IntelliSkin® Thin-Case™",
                            "GDS® Uni-Conn™",
                            "Kiosk, Desktop & Wall Docks",
                            "Shoulder Strap, Hand Strap & Kickstand Accessories"
                        ],
                        recommendations: "Solutions for this environment do not include any vehicle mounts. IntelliSkin® Thin-Case™ does not support Samsung DeX or Zebra Workstation Connect. IntelliSkin® Thin-Case™ is not available with any LED Lighting option. For solutions that support these options, see Standard or Heavy Duty Environments."
                    }
                }
            },
            "Standard": {
                "Standard Duty Use": {
                    image: "https://cdn.shopify.com/s/files/1/0593/5346/4982/files/StandardDutyUse.jpg?v=1745854127",
                    features: ["Car & Truck", "Utility & Field Service", "Commercial Trucking", "Last Mile Delivery", "Student & Public Transportation"],
                    modalInfo: {
                        title: "Standard Duty Environment",
                        idealFor: ["Delivery vehicles", "Field service teams", "Sales representatives", "Mobile clinics"],
                        options: [
                            "IntelliSkin® Next Gen & Thin-Case™",
                            "GDS® Uni-Conn™",
                            "Vehicle, Desktop & Wall Docks",
                            "Mounting Options (Universal)",
                            "Temperature Control",
                            "Shoulder Strap, Hand Strap & Kickstand Accessories"
                        ],
                        recommendations: "Solutions for this environment do not include GDS® Tough-Dock™, keyboard mounting or vehicle-specific options. For these solutions see Heavy Duty Environments."
                    }
                }
            },
            "Heavy Duty": {
                "Heavy Duty Use": {
                    image: "https://cdn.shopify.com/s/files/1/0593/5346/4982/files/HeavyDutyUse_f802c8d5-f74d-4f4e-90e7-379def9b3fdc.jpg?v=1745854127",
                    features: ["Heavy Equipment", "Harsh Environments", "Rough Roads", "Construction Sites", "Material Handling / Warehouses"],
                    modalInfo: {
                        title: "Heavy Duty Environment",
                        options: [
                            "IntelliSkin® Next Gen",
                            "GDS® Tough-Dock™",
                            "Vehicle & Desktop Docks",
                            "Mounting Options (Vehicle Specific & Universal)",
                            "Temperature Control",
                            "Keyboard Mounting Options",
                            "GDS™ Roto-Mag™, GDS® Hand-Stand™ & Shoulder Strap Accessories"
                        ],
                        recommendations: "IntelliSkin® Thin-Case™ and GDS® Uni-Conn™ are not recommended for heavy duty environments. For these options, see Standard Environments"
                    }
                }
            }
        };
    }

    _getSortedEnvironmentOptions(options) {
        const desiredOrder = ["Light Duty", "Standard", "Heavy Duty"];
        return [...options].sort((a, b) => {
            const indexA = desiredOrder.indexOf(a);
            const indexB = desiredOrder.indexOf(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }

    _getEnvironmentGridLayout(numOptions) {
        if (numOptions === 1) return { gridColsClass: 'grid-cols-1', gridContainerClass: 'max-w-md mx-auto' };
        if (numOptions === 2) return { gridColsClass: 'grid-cols-1 md:grid-cols-2', gridContainerClass: 'max-w-3xl mx-auto' };
        if (numOptions === 3) return { gridColsClass: 'grid-cols-1 md:grid-cols-3', gridContainerClass: 'max-w-5xl mx-auto' };
        return { gridColsClass: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', gridContainerClass: 'max-w-6xl mx-auto' };
    }

    _buildEnvironmentCardHTML(envOption, detailsMap) {
        const envKey = Object.keys(detailsMap).find(key => key.includes(envOption));
        if (!envKey) return '';
        const fullOptionName = Object.keys(detailsMap[envKey])[0];
        const envInfo = detailsMap[envKey][fullOptionName];
        if (!envInfo) return '';
        const featuresList = envInfo.features.map(feature => `
            <li class="flex items-center text-sm mb-2">
                <svg class="h-4 w-4 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>
                ${feature}
            </li>
        `).join('');

        return `
            <div class="bg-white rounded-3xl overflow-hidden shadow-sm cursor-pointer transition-all hover:shadow-md environment-card"
                 data-environment-api="${envKey}">
                <div class="h-42 overflow-hidden"><img src="${envInfo.image}" alt="${fullOptionName}" class="w-full h-full object-cover"></div>
                <div class="p-4 relative min-h-[200px]">
                    <h3 class="font-bold text-lg mb-2">${fullOptionName}</h3>
                    <ul class="space-y-1 mb-8">${featuresList}</ul>
                    <div class="absolute bottom-3 right-3 w-8 h-8 transition-transform duration-300 hover:scale-110 cursor-pointer info-icon-container"
                         data-modal-target="${envKey.replace(/\s+/g, '-').toLowerCase()}-modal">
                         <svg class="info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path clip-rule="evenodd" d="m12 3.75c-4.55635 0-8.25 3.69365-8.25 8.25 0 4.5563 3.69365 8.25 8.25 8.25 4.5563 0 8.25-3.6937 8.25-8.25 0-4.55635-3.6937-8.25-8.25-8.25zm-9.75 8.25c0-5.38478 4.36522-9.75 9.75-9.75 5.3848 0 9.75 4.36522 9.75 9.75 0 5.3848-4.3652 9.75-9.75 9.75-5.38478 0-9.75-4.3652-9.75-9.75zm9.75-.75c.4142 0 .75.3358.75.75v3.5c0 .4142-.3358.75-.75.75s-.75-.3358-.75-.75v-3.5c0-.4142.3358-.75.75-.75zm0-3.25c-.5523 0-1 .44772-1 1s.4477 1 1 1h.01c.5523 0 1-.44772 1-1s-.4477-1-1-1z" fill="#787878" fill-rule="evenodd"></path></g></svg>
                    </div>
                </div>
            </div>
        `;
    }

    _buildEnvironmentModalHTML(envOption, detailsMap) {
        const envKey = Object.keys(detailsMap).find(key => key.includes(envOption));
        if (!envKey) return '';
        const fullOptionName = Object.keys(detailsMap[envKey])[0];
        const envInfo = detailsMap[envKey][fullOptionName];
        if (!envInfo || !envInfo.modalInfo) return '';
        const modalId = `${envKey.replace(/\s+/g, '-').toLowerCase()}-modal`;
        const modalInfo = envInfo.modalInfo;
        const optionsList = (modalInfo.options || []).map(option => `
            <li class="flex items-start">
                <span class="text-green-500 mr-2">•</span>
                <span class="text-gray-600">${option}</span>
            </li>
        `).join('');

        return `
            <div id="${modalId}" class="fixed inset-0 bg-black bg-opacity-60 z-[100] hidden flex items-center justify-center modal-container" data-modal-type="environment">
                <div class="absolute inset-0 bg-black bg-opacity-60 z-[101]" data-backdrop="true"></div>
                <div class="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl relative z-[102]">
                    <div class="flex justify-between items-center p-5 border-b border-gray">
                        <h3 class="text-xl font-bold text-gray-800">${modalInfo.title}</h3>
                        <button type="button" class="modal-close text-gray-400 hover:text-gray-600"><svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
                    </div>
                    <div class="p-6 space-y-6">
                        <div class="bg-green-50 rounded-lg p-4">
                            <h4 class="text-lg font-medium text-gray-800 mb-3 flex items-center"><svg class="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z"></path></svg>Options Include:</h4>
                            <ul class="space-y-2 pl-4">${optionsList}</ul>
                        </div>
                        <div class="border border-gray rounded-lg p-4">
                            <h4 class="text-lg font-medium text-gray-800 mb-3 flex items-center"><svg class="w-5 h-5 mr-2 text-red" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"></path></svg>Limitations and Restrictions:</h4>
                            <p class="text-gray-600 leading-relaxed">${modalInfo.recommendations}</p>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    collectFormData() {
        const updatedContext = { ...(this.state.context.context || {}) };
        const envInput = this.contentContainer.querySelector('input[data-field="environment"]');

        if (envInput?.value) {
            updatedContext['environment'] = envInput.value;
            return updatedContext;
        } else {
            this.builder.uiFeedback.showError('Please select an environment.');
            return null;
        }
    }

    getButtonConfig() {
        return {
            innerHTML: 'Next <svg class="h-5 w-5 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>',
            isVisible: false,
            isEnabled: false
        };
    }
}
